import { rerouteErdElements, isErdArrowElement } from './erdParser';

const OBSTACLE_MARGIN = 8;
const ROUTE_GAP = 36;
const STUB = 28;

const getClassBox = (el) => {
  if (!el || el.type !== 'erd-class') return null;
  if (typeof el.x !== 'number' || typeof el.y !== 'number') return null;
  if (typeof el.width !== 'number' || typeof el.height !== 'number') return null;
  return { id: el.id, x: el.x, y: el.y, width: el.width, height: el.height };
};

const getArrowPoints = (arrow) =>
  arrow.pathPoints?.length >= 2
    ? arrow.pathPoints
    : [
        { x: arrow.x, y: arrow.y },
        { x: arrow.x2, y: arrow.y2 },
      ];

const rangesOverlap = (a1, a2, b1, b2) => Math.max(a1, b1) < Math.min(a2, b2);

const segmentIntersectsBox = (a, b, box) => {
  const left = box.x - OBSTACLE_MARGIN;
  const right = box.x + box.width + OBSTACLE_MARGIN;
  const top = box.y - OBSTACLE_MARGIN;
  const bottom = box.y + box.height + OBSTACLE_MARGIN;

  if (Math.abs(a.y - b.y) < 0.5) {
    return a.y > top && a.y < bottom && rangesOverlap(Math.min(a.x, b.x), Math.max(a.x, b.x), left, right);
  }

  if (Math.abs(a.x - b.x) < 0.5) {
    return a.x > left && a.x < right && rangesOverlap(Math.min(a.y, b.y), Math.max(a.y, b.y), top, bottom);
  }

  return false;
};

const pointInsideBox = (point, box) =>
  point.x > box.x - OBSTACLE_MARGIN &&
  point.x < box.x + box.width + OBSTACLE_MARGIN &&
  point.y > box.y - OBSTACLE_MARGIN &&
  point.y < box.y + box.height + OBSTACLE_MARGIN;

const segmentsIntersect = (a, b, c, d) => {
  const cross = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const ab1 = cross(a, b, c);
  const ab2 = cross(a, b, d);
  const cd1 = cross(c, d, a);
  const cd2 = cross(c, d, b);
  return ab1 * ab2 <= 0 && cd1 * cd2 <= 0;
};

const lineIntersectsBox = (a, b, box) => {
  if (segmentIntersectsBox(a, b, box)) return true;
  if (pointInsideBox(a, box) || pointInsideBox(b, box)) return true;

  const left = box.x - OBSTACLE_MARGIN;
  const right = box.x + box.width + OBSTACLE_MARGIN;
  const top = box.y - OBSTACLE_MARGIN;
  const bottom = box.y + box.height + OBSTACLE_MARGIN;
  const corners = [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];

  return corners.some((corner, idx) => segmentsIntersect(a, b, corner, corners[(idx + 1) % corners.length]));
};

const getBoardRouteNeed = (arrow, boxes) => {
  const points = getArrowPoints(arrow);
  if (points.length !== 2) return { shouldRoute: false, blockers: [] };

  const [start, end] = points;
  const horizontal = Math.abs(start.y - end.y) < 0.5;
  const vertical = Math.abs(start.x - end.x) < 0.5;

  const startId = arrow.startConnection?.elementId;
  const endId = arrow.endConnection?.elementId;

  const blockers = boxes.filter((box) => {
    if (box.id === startId || box.id === endId) return false;
    return lineIntersectsBox(start, end, box);
  });

  return {
    shouldRoute: (!horizontal && !vertical) || blockers.length > 0,
    blockers,
  };
};

const buildObstacleRoute = (arrow, blockers, boxById) => {
  const [start, end] = getArrowPoints(arrow);
  const startBox = boxById.get(arrow.startConnection?.elementId);
  const endBox = boxById.get(arrow.endConnection?.elementId);
  const routeBoxes = [startBox, endBox, ...blockers].filter(Boolean);

  const horizontal =
    arrow.erdRoute?.dominantAxis === 'horizontal' ||
    Math.abs(end.x - start.x) >= Math.abs(end.y - start.y);

  if (horizontal) {
    const direction = end.x >= start.x ? 1 : -1;
    const laneY = Math.min(...routeBoxes.map((box) => box.y)) - ROUTE_GAP;
    const startStubX = start.x + STUB * direction;
    const endStubX = end.x - STUB * direction;
    return [
      start,
      { x: startStubX, y: start.y },
      { x: startStubX, y: laneY },
      { x: endStubX, y: laneY },
      { x: endStubX, y: end.y },
      end,
    ];
  }

  const direction = end.y >= start.y ? 1 : -1;
  const laneX = Math.min(...routeBoxes.map((box) => box.x)) - ROUTE_GAP;
  const startStubY = start.y + STUB * direction;
  const endStubY = end.y - STUB * direction;
  return [
    start,
    { x: start.x, y: startStubY },
    { x: laneX, y: startStubY },
    { x: laneX, y: endStubY },
    { x: end.x, y: endStubY },
    end,
  ];
};

const getLabelAnchor = (points) => {
  if (!points?.length) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];

  let best = { from: points[0], to: points[1], length: -1 };
  for (let idx = 0; idx < points.length - 1; idx += 1) {
    const from = points[idx];
    const to = points[idx + 1];
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (length > best.length) best = { from, to, length };
  }

  return {
    x: (best.from.x + best.to.x) / 2,
    y: (best.from.y + best.to.y) / 2,
  };
};

export const applyErdBoardLayout = (elements) => {
  const routed = rerouteErdElements(elements);
  const boxes = routed.map(getClassBox).filter(Boolean);
  if (!boxes.length) return routed;

  const boxById = new Map(boxes.map((box) => [box.id, box]));
  const changedArrowIds = new Set();

  const withObstacleRoutes = routed.map((el) => {
    if (!isErdArrowElement(el)) return el;

    const { shouldRoute, blockers } = getBoardRouteNeed(el, boxes);
    if (!shouldRoute) return el;

    const pathPoints = buildObstacleRoute(el, blockers, boxById);
    changedArrowIds.add(el.id);
    return {
      ...el,
      pathPoints,
      erdRoute: {
        ...(el.erdRoute || {}),
        bent: true,
        routedAroundObstacles: true,
      },
    };
  });

  if (!changedArrowIds.size) return withObstacleRoutes;

  const arrowById = new Map();
  withObstacleRoutes.forEach((el) => {
    if (el.type === 'arrow') arrowById.set(el.id, el);
  });

  return withObstacleRoutes.map((el) => {
    if (el.type !== 'text' || !changedArrowIds.has(el.attachedToArrow)) return el;
    const arrow = arrowById.get(el.attachedToArrow);
    if (!arrow) return el;
    const anchor = getLabelAnchor(getArrowPoints(arrow));
    return { ...el, x: anchor.x, y: anchor.y };
  });
};
